/**
 * Mystery gift bar.
 *  1. Live UI: on every cart change, recompute the progress bar, "faltam X"
 *     text and unlocked state for each [data-gift-bar] (drawer + cart page).
 *  2. Auto-add/remove: adds the free gift (qty 1) once the qualifying subtotal
 *     reaches the threshold, removes it if the cart drops below, caps it at 1.
 *
 * We watch <cart-element> for the theme's re-render (the quantity event fires
 * BEFORE the cart commits, so DOM re-render is the reliable "committed" signal).
 * IMPORTANT: our own paint() also mutates inside <cart-element>, so observers
 * are paused around paint to avoid an infinite observe -> paint -> observe loop.
 */
(function () {
  "use strict";
  var busy = false;
  var observers = [];
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  function getCart() {
    return fetch(root + "cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  function postJSON(url, body) {
    return fetch(root + url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    });
  }

  function refreshTheme() {
    document.dispatchEvent(new CustomEvent("theme:cart:refresh", { bubbles: true }));
  }

  function formatMoney(cents, currency) {
    var value = (cents || 0) / 100;
    try {
      return value.toLocaleString("pt-BR", { style: "currency", currency: currency || "BRL" });
    } catch (e) {
      return "R$ " + value.toFixed(2).replace(".", ",");
    }
  }

  function disconnectObservers() {
    observers.forEach(function (o) {
      o.disconnect();
    });
    observers = [];
  }

  function connectObservers() {
    if (!("MutationObserver" in window)) return;
    disconnectObservers();
    document.querySelectorAll("cart-element").forEach(function (el) {
      var mo = new MutationObserver(scheduleReconcile);
      mo.observe(el, { childList: true, subtree: true, characterData: true });
      observers.push(mo);
    });
  }

  // Recompute + repaint every gift bar from the current cart. Returns the state
  // for the first bar (config is identical across bars).
  function paint(cart) {
    var bars = document.querySelectorAll("[data-gift-bar]");
    if (!bars.length) return null;

    var out = null;
    bars.forEach(function (bar) {
      var variant = parseInt(bar.getAttribute("data-gift-variant"), 10);
      var threshold = parseInt(bar.getAttribute("data-gift-threshold"), 10);
      if (!variant || !threshold) return;

      var qualifying = 0;
      var giftItem = null;
      (cart.items || []).forEach(function (item) {
        if (item.variant_id === variant) giftItem = item;
        else qualifying += item.final_line_price;
      });

      var reached = qualifying >= threshold;
      var remaining = Math.max(threshold - qualifying, 0);
      var percent = Math.min(Math.round((qualifying * 100) / threshold), 100);
      var amount = '<strong class="gift-bar__amt">' + formatMoney(remaining, cart.currency) + "</strong>";

      // Track fill + marker position.
      var fill = bar.querySelector("[data-gift-fill]");
      if (fill) fill.style.width = percent + "%";
      var marker = bar.querySelector("[data-gift-marker]");
      if (marker) marker.style.left = percent + "%";

      // Headline (progress line) + subtext.
      var titleTpl = bar.getAttribute("data-gift-message") || "";
      var progressLine = bar.querySelector(".gift-bar__title-progress");
      if (progressLine) progressLine.innerHTML = titleTpl.replace("||amount||", amount);

      var subTpl = bar.getAttribute("data-gift-subtext") || "";
      var sub = bar.querySelector("[data-gift-sub]");
      if (sub) sub.innerHTML = subTpl.replace("||amount||", amount);

      bar.classList.toggle("is-success", reached);
      // Reveal once the cart has items; hide again when empty.
      bar.classList.toggle("gift-bar--hidden", (cart.item_count || 0) === 0);

      out = { variant: variant, threshold: threshold, reached: reached, giftItem: giftItem };
    });
    return out;
  }

  function reconcile() {
    if (busy) return;
    busy = true;

    getCart()
      .then(function (cart) {
        // Pause observers while we mutate the bars, then resume.
        disconnectObservers();
        var state = paint(cart);
        setTimeout(connectObservers, 50);

        if (!state) {
          busy = false;
          return;
        }

        if (state.reached && !state.giftItem) {
          return postJSON("cart/add.js", { items: [{ id: state.variant, quantity: 1 }] }).then(function (res) {
            busy = false;
            if (res.ok) refreshTheme();
          });
        }
        if (!state.reached && state.giftItem) {
          return postJSON("cart/change.js", { id: state.giftItem.key, quantity: 0 }).then(function () {
            busy = false;
            refreshTheme();
          });
        }
        if (state.reached && state.giftItem && state.giftItem.quantity > 1) {
          return postJSON("cart/change.js", { id: state.giftItem.key, quantity: 1 }).then(function () {
            busy = false;
            refreshTheme();
          });
        }
        busy = false;
      })
      .catch(function () {
        busy = false;
      });
  }

  var timer = null;
  function scheduleReconcile() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 150);
  }

  ["theme:cart:added", "theme:cart:loaded"].forEach(function (evt) {
    document.addEventListener(evt, scheduleReconcile);
  });

  function init() {
    connectObservers();
    reconcile();
  }
  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
})();
